import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import AuthModal from '../modals/AuthModal';
import UnsubscribeModal from '../modals/UnsubscribeModal';
import brand from '../../config/brand';

const categories = [
  { slug: 'news', name: 'News' },
  { slug: 'personal-finance', name: 'Personal Finance' },
  { slug: 'debt-management', name: 'Debt Management' },
  { slug: 'small-business', name: 'Small Business' },
  { slug: 'investing', name: 'Investing' },
  { slug: 'markets', name: 'Markets' },
];

export default function Footer() {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [unsubscribeModalOpen, setUnsubscribeModalOpen] = useState(false);

  return (
    <footer className="footer bg-black pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <span className="text-2xl font-black uppercase tracking-tighter" style={{ fontFamily: "'Merriweather', serif" }}>
                <span className="text-red-500">{brand.namePart1}</span>
                <span className="text-white">{brand.namePart2}</span>
              </span>
            </Link>
            <p className="text-zinc-500 text-sm leading-relaxed mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Your trusted source for financial news, market insights, and personal finance guidance. 
              Stay informed, make smarter decisions.
            </p>
            <div className="flex space-x-3">
              <a href="#" className="text-zinc-600 hover:text-red-500 transition-colors p-2 border border-zinc-800 hover:border-red-500" aria-label="Facebook">
                <Facebook className="w-4 h-4" strokeWidth={2} />
              </a>
              <a href="#" className="text-zinc-600 hover:text-red-500 transition-colors p-2 border border-zinc-800 hover:border-red-500" aria-label="Twitter">
                <Twitter className="w-4 h-4" strokeWidth={2} />
              </a>
              <a href="#" className="text-zinc-600 hover:text-red-500 transition-colors p-2 border border-zinc-800 hover:border-red-500" aria-label="Instagram">
                <Instagram className="w-4 h-4" strokeWidth={2} />
              </a>
              <a href="#" className="text-zinc-600 hover:text-red-500 transition-colors p-2 border border-zinc-800 hover:border-red-500" aria-label="LinkedIn">
                <Linkedin className="w-4 h-4" strokeWidth={2} />
              </a>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-white font-mono font-bold mb-5 text-xs uppercase tracking-widest border-b border-zinc-800 pb-3">Categories</h4>
            <ul className="space-y-2">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="text-zinc-500 hover:text-white text-sm transition-colors font-medium"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-mono font-bold mb-5 text-xs uppercase tracking-widest border-b border-zinc-800 pb-3">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/advertise" className="text-zinc-500 hover:text-white text-sm transition-colors font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Advertise With Us
                </Link>
              </li>
              <li>
                <Link to="/archive" className="text-zinc-500 hover:text-white text-sm transition-colors font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Archive
                </Link>
              </li>
              <li>
                <a href="#" className="text-zinc-500 hover:text-white text-sm transition-colors font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Contact
                </a>
              </li>
              <li>
                <Link to="/privacy" className="text-zinc-500 hover:text-white text-sm transition-colors font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-zinc-500 hover:text-white text-sm transition-colors font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Terms of Service
                </Link>
              </li>
              <li>
                <button 
                  onClick={() => setUnsubscribeModalOpen(true)}
                  className="text-zinc-500 hover:text-white text-sm transition-colors font-medium"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                  data-testid="unsubscribe-link"
                >
                  Unsubscribe
                </button>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-white font-mono font-bold mb-5 text-xs uppercase tracking-widest border-b border-zinc-800 pb-3">Newsletter</h4>
            <p className="text-zinc-500 text-sm mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Get the latest financial news delivered to your inbox.
            </p>
            <Button
              onClick={() => setAuthModalOpen(true)}
              className="w-full bg-red-600 text-white hover:bg-white hover:text-black font-mono font-bold uppercase tracking-widest text-xs rounded-none border-0 transition-colors"
              data-testid="footer-subscribe-btn"
            >
              <Mail className="w-4 h-4 mr-2" strokeWidth={2} />
              Subscribe
            </Button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-zinc-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-zinc-600 text-xs font-mono uppercase tracking-wider">
              {new Date().getFullYear()} {brand.name}. All Rights Reserved.
            </p>
            <div className="flex items-center space-x-4">
              <a href={`mailto:${brand.contactEmail}`} className="flex items-center text-zinc-500 hover:text-white text-xs transition-colors font-mono tracking-wider">
                <Mail className="w-3 h-3 mr-2" strokeWidth={2} />
                {brand.contactEmail}
              </a>
            </div>
          </div>
        </div>
      </div>
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} initialMode="register" />
      <UnsubscribeModal open={unsubscribeModalOpen} onClose={() => setUnsubscribeModalOpen(false)} />
    </footer>
  );
}
